import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStripe } from '@stripe/react-stripe-js';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useFetchData } from 'services/api/useFetchData';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import ModuleOrderCard from './ModuleOrderCard';
import { formatPriceOnUserLocale } from './functions/billingUtils';
import { handleManualPayment } from './functions/handleManualPayment';
import { clearModuleFeaturesCache } from './functions/useOrderableModuleFeatures';
import '../../../style/scss/billing.scss';

interface ModuleOrderModalProps {
    feature: string;
    onSuccess?: () => void;
};

const ModuleOrderModal: React.FC<ModuleOrderModalProps> = ({ feature, onSuccess }) => {
    const { t } = useTranslation();
    const stripe = useStripe();
    const { closeModal, revealModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const { userLocale } = useSettings();
    const { environmentHash, handleLogout } = useAuthContext();
    const [buttonLoader, setButtonLoader] = useState<boolean>(false);

    // Fetch the order details of the module
    const { response: orderDetails, loading } = useFetchData({
        apiUrl: 'get_module_order_details',
        params: useMemo(() => ({ module: feature }), [feature])
    });

    // Reveal the modal after the order details are fetched
    useEffect(() => {
        if (orderDetails) {
            revealModal();
        }
    }, [orderDetails, revealModal]);

    // Show an alert and close when the order details cannot be fetched, for example without a subscription
    useEffect(() => {
        if (loading === 'error') {
            setFloatingAlert({ type: 'warning', message: 'billing.general.order_requires_subscription_message' });
            closeModal();
        }
    }, [loading, setFloatingAlert, closeModal]);

    // Render nothing until the order details are fetched
    if (!orderDetails) {
        return null;
    }

    // Get the total amounts for the remaining subscription period
    const totalIncVat = parseFloat(orderDetails.total_inc_vat);

    // Handle the order and payment of the module
    const handleOrder = async () => {
        try {
            // Start the button loader
            setButtonLoader(true);

            // Order the module in the backend as inactive line awaiting payment
            const response = await saveData({
                apiUrl: `post_module/${environmentHash}`,
                method: 'post',
                data: { module: feature }
            });

            // Handle successful response
            if (response?.status === 200) {

                // Start the ideal payment when an amount is due, the module activates after the payment
                if (response.data.start_manual_payment === true) {

                    // Fetch the main company details for the ideal billing details
                    const companyDetailsResponse = await fetchData({
                        apiUrl: `get_main_company_details/${environmentHash}`,
                        handleLogout
                    });

                    // Redirect to the ideal payment page, the loader keeps running until the redirect
                    // The current path is passed so the thank you page can link back to this menu
                    await handleManualPayment(
                        stripe,
                        environmentHash,
                        companyDetailsResponse,
                        response.data.subscription_id,
                        null,
                        response.data.new_module_line_ids,
                        null,
                        null,
                        null,
                        window.location.pathname
                    );

                    return;
                }

                // Without an amount due the module is activated directly
                setButtonLoader(false);
                clearModuleFeaturesCache();
                setFloatingAlert({ type: 'success', message: 'billing.general.module_order_activated_message' });

                // Call the on success callback
                if (onSuccess) {
                    onSuccess();
                }

                // Close the modal
                closeModal();
            }
        } catch (error) {
            console.error('Error on module order', error);

            // Stop the button loader
            setButtonLoader(false);

            // Show an alert that the order could not be completed
            setFloatingAlert({ type: 'danger', message: 'billing.general.order_failed_message' });
        };
    };

    return (
        <div className='module-order-modal'>

            {/* Show the modal header with a short introduction */}
            <h3>{t('billing.general.module_order_modal_title')}</h3>
            <p>{t('billing.general.module_order_modal_description')}</p>

            {/* Show the module card without a frame since there is no choice to make */}
            <ModuleOrderCard
                feature={feature}
                title={t(`billing.feature.${feature}`)}
                description={orderDetails.description ?? undefined}
                priceType={orderDetails.price_type}
                flatPrice={orderDetails.flat_price}
                pricePerSeat={orderDetails.price_per_seat}
                frameless={true}
                showOrderLink={false}
            />

            {/* Show the payment explanation with the amounts for the remaining period */}
            {totalIncVat > 0 && (
                <p className='helper-text'>
                    {t('billing.general.module_order_payment_explanation', {
                        totalExVat: formatPriceOnUserLocale(parseFloat(orderDetails.total_ex_vat), userLocale),
                        months: orderDetails.months_remaining.replace('.', ','),
                        totalIncVat: formatPriceOnUserLocale(totalIncVat, userLocale)
                    })}
                </p>
            )}

            {/* The modal buttons */}
            <div className='button-row'>
                <div className='buttons-right'>
                    <SecondaryButton
                        type='button'
                        label='general.cancel'
                        size='small'
                        onClick={() => closeModal()}
                    />
                    <PrimaryButton
                        type='button'
                        label={totalIncVat > 0 ? 'billing.general.module_order_pay_label' : 'billing.general.module_order_activate_label'}
                        size='small'
                        loading={buttonLoader}
                        onClick={handleOrder}
                    />
                </div>
            </div>
        </div>
    );
};

export default ModuleOrderModal;
